//import { useGrid } from "common";
import { globalMessageHandler } from "common";
import { useParams } from "common/components/Form/ParamsContext";
import { useFilter } from "components/FilterProvider";
import useDictionaries from "features/dictionaries/useDictionaries";
import { useMemo, useState } from "react";
import {
  BookingItem,
  cancelArmband,
  cancelBookingsByIds,
  checkIn,
  checkOut,
  getBookingService,
  registerArmband,
} from "./bookingApi";
import { BOOKING_STATUSES } from "./bookingConstants";

export type BookingFilter = {
  startTime?: string;
  programIds?: number[];
  eventIds?: number[];
  boatIds?: number[];
  created_by?: number[];
  partner?: number[];
  partnerName?: string;
  source?: number[];
  partnerGroup?: number[];
  status?: BOOKING_STATUSES[];
  types?: Array<"scheduled" | "planned">;
  range?: { start?: string; end?: string };
  refund?: boolean;
};
export default function useBookingList(id: string, customScope?: string) {
  const params = useParams<
    | "event_id"
    | "boat_id"
    | "program_id"
    | "schedule_id"
    | "program_variant_id"
    | "partner_id"
    | "partner_group_id"
    | "sales_person_id"
  >();
  const [filter, onChange] = useFilter<BookingFilter>();
  const [showMore, setShowMore] = useState<boolean>();
  const { dictionaries } = useDictionaries();
  const listId = `bookings_${id}`;

  const scope = {
    events: params.event_id,
    boats: params.boat_id,
    programs: params.program_id,
    schedules: params.schedule_id,
    "program-variants": params.program_variant_id,
    partners: params.partner_id,
    "partner-groups": params.partner_group_id,
    "sales-persons": params.sales_person_id,
  };

  const editUrl = `${
    Object.values(scope || {}).filter(v => !!v).length > 0 ? "bookings/" : ""
  }`;
  const service = getBookingService(scope, customScope);

  async function checkInAsync(booking: BookingItem) {
    //setCheckInLoading(true);
    const { data, success } = await checkIn(booking.id);
    //setCheckInLoading(false);
    if (success) {
      globalMessageHandler.snack({
        message: "Sikeres vendég érkeztetés.",
      });
    }
  }
  async function checkOutAsync(booking: BookingItem) {
    //setCheckOutLoading(true);
    const { data, success } = await checkOut(booking.id);
    //setCheckOutLoading(false);
    if (success) {
      globalMessageHandler.snack({
        message: "Sikeresen törölted a vendég érkeztetést.",
      });
    }
  }
  async function registerArmbandAsync(booking: BookingItem) {
    //setCheckInLoading(true);
    const { data, success } = await registerArmband(booking.id);
    //setCheckInLoading(false);
    if (success) {
      globalMessageHandler.snack({
        message: "Sikeres karazalag rögzítés.",
      });
    }
  }
  async function cancelArmbandAsync(booking: BookingItem) {
    //setCheckOutLoading(true);
    const { data, success } = await cancelArmband(booking.id);
    //setCheckOutLoading(false);
    if (success) {
      globalMessageHandler.snack({
        message: "Sikeresen törölted a karszalagot.",
      });
    }
  }
  const gridFilter = useMemo(() => {
    let f: any = [];

    if (filter?.status && filter?.status.length > 0) {
      f.push({
        columnField: "booking.status",
        operatorValue: "in",
        value: filter?.status,
      });
    }
    if (filter?.types) {
      if (filter?.types.length === 1) {
        if (filter?.types[0] === "planned") {
          f.push({
            columnField: "booking.event_id",
            operatorValue: "empty",
          });
        } else {
          f.push({
            columnField: "booking.event_id",
            operatorValue: "notEmpty",
          });
        }
      }
    }

    if (filter?.range?.start && filter?.range?.end) {
      f.push({
        columnField: "estimated_start_time",
        operatorValue: "between",
        value: [filter?.range?.start, `${filter?.range?.end} 23:59:59`],
      });
    }

    if (filter?.programIds && filter?.programIds.length > 0) {
      f.push({
        columnField: "event.program_id",
        operatorValue: "in",
        value: filter?.programIds,
      });
    }
    if (filter?.eventIds && filter?.eventIds.length > 0) {
      f.push({
        columnField: "event_id",
        operatorValue: "in",
        value: filter?.eventIds,
      });
    }
    if (!!filter?.startTime) {
      f.push({
        columnField: "estimated_start_time",
        operatorValue: "contains",
        value: filter?.startTime,
      });
    }
    if (filter?.boatIds && filter?.boatIds.length > 0) {
      f.push({
        columnField: "event_group.boat_id",
        operatorValue: "in",
        value: filter?.boatIds,
      });
    }
    if (filter?.created_by && filter?.created_by.length > 0) {
      f.push({
        columnField: "booking.created_by",
        operatorValue: "in",
        value: filter?.created_by,
      });
    }
    if (filter?.partner && filter?.partner.length > 0) {
      f.push({
        columnField: "booking.partner_id",
        operatorValue: "in",
        value: filter?.partner,
      });
    }
    if (filter?.partnerName && filter?.partnerName.trim() !== "") {
      f.push({
        columnField: "partnerName",
        operatorValue: "like",
        value: filter?.partnerName,
      });
    }
    if (filter?.source && filter?.source.length > 0) {
      f.push({
        columnField: "booking.source_id",
        operatorValue: "in",
        value: filter?.source,
      });
    }
    if (filter?.partnerGroup && filter?.partnerGroup.length > 0) {
      f.push({
        columnField: "partner.partner_group_id",
        operatorValue: "in",
        value: filter?.partnerGroup,
      });
    }
    if (filter?.refund) {
      f.push({
        columnField: "booking.refund_amount",
        operatorValue: "notEmpty",
      });
    }
    return f;
  }, [filter]);

  async function cancelBookingsByIdsAsync(bookingIds: any[], load: () => void) {
    let event_id: any = 1;
    // setGenerating(true);
    globalMessageHandler.confirm(
      {
        message: `Biztosan visszavosz ${bookingIds?.length || 0} rendelést?`,
      },
      async () => {
        const { success, data } = await cancelBookingsByIds(bookingIds);

        //setGenerating(false);
        if (success) {
          globalMessageHandler.snack({
            severity: "success",
            message: `Sikeresen visszavonva ${data.count} foglalás`,
          });
          load();
        }
      }
    );
  }

  return {
    ...service,
    params,
    scope,
    editUrl,
    listId,
    checkInAsync,
    checkOutAsync,
    filter,
    gridFilter,
    onChange,
    showMore,
    setShowMore,
    cancelBookingsByIdsAsync,
    registerArmbandAsync,
    cancelArmbandAsync,
    users: dictionaries.users,
    partners: dictionaries.partners,
    partnergroups: dictionaries.partnergroups,
  };
}
